import { useRef, useState } from "react"
import type { KeyboardEvent, ReactNode } from "react"
import { useSearchParams } from "react-router-dom"

export type TabItem = {
  id: string
  label: ReactNode
  /** 제목 옆 작은 수(품목 건수 등). */
  badge?: ReactNode
  content: ReactNode
}

/**
 * 탭. 키보드로 ←→/Home/End 가 되고, 선택한 탭만 Tab 키 순회에 들어간다(roving tabindex).
 *
 * `param` 을 주면 선택이 URL 에 남아 새로고침·링크 공유가 그대로 된다.
 * 옆판과 전체 페이지가 같은 파라미터를 쓰므로 어느 쪽에서 열어도 같은 탭이 보인다.
 */
export function Tabs({
  items,
  param = "tab",
  ariaLabel,
}: {
  items: TabItem[]
  /** URL 파라미터 이름. null 이면 컴포넌트 안에서만 기억한다. */
  param?: string | null
  ariaLabel: string
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [localId, setLocalId] = useState(items[0]?.id ?? "")
  const listRef = useRef<HTMLDivElement>(null)

  const fromUrl = param === null ? null : searchParams.get(param)
  const raw = param === null ? localId : fromUrl
  // URL 에 모르는 값이 들어와도 첫 탭으로 되돌린다.
  const activeId = items.some((item) => item.id === raw) ? (raw as string) : (items[0]?.id ?? "")

  function select(id: string) {
    if (param === null) {
      setLocalId(id)
      return
    }
    const next = new URLSearchParams(searchParams)
    next.set(param, id)
    // 탭 전환이 뒤로가기 기록을 채우면 안 된다.
    setSearchParams(next, { replace: true })
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.id === activeId)
    let nextIndex: number | null = null
    if (event.key === "ArrowRight") nextIndex = (index + 1) % items.length
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + items.length) % items.length
    else if (event.key === "Home") nextIndex = 0
    else if (event.key === "End") nextIndex = items.length - 1
    if (nextIndex === null) return

    event.preventDefault()
    const target = items[nextIndex]
    select(target.id)
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#tab-${CSS.escape(target.id)}`)
      ?.focus()
  }

  const active = items.find((item) => item.id === activeId)

  return (
    <div className="tabs">
      <div className="tabs__list" role="tablist" aria-label={ariaLabel} ref={listRef} onKeyDown={onKeyDown}>
        {items.map((item) => {
          const selected = item.id === activeId
          return (
            <button
              key={item.id}
              type="button"
              id={`tab-${item.id}`}
              role="tab"
              className={selected ? "tabs__tab is-active" : "tabs__tab"}
              aria-selected={selected}
              aria-controls={`panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(item.id)}
            >
              {item.label}
              {item.badge !== undefined && item.badge !== null ? (
                <span className="tabs__badge">{item.badge}</span>
              ) : null}
            </button>
          )
        })}
      </div>

      {active ? (
        <div
          id={`panel-${active.id}`}
          role="tabpanel"
          aria-labelledby={`tab-${active.id}`}
          tabIndex={0}
          className="tabs__panel"
        >
          {active.content}
        </div>
      ) : null}
    </div>
  )
}
